import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { resolveWesenQuestion } from '../lib/wesenQuestionsByAge';
import VoiceTextarea from './VoiceTextarea';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  Fingerprint, MagnifyingGlass, BookOpen, Scales, Lightbulb, Users,
  Binoculars, Sword, Timer, ShieldCheck, Heart, HandHeart,
  UsersThree, HandshakeIcon, Crown, Flower, SmileySad, Eye as EyeIcon,
  SunHorizon, Sparkle, HandsPraying, SmileyWink, Cross,
  ArrowRight, Check, CaretUp, CaretDown, Warning, List,
  Star, Lightning, Brain, Target, TreeStructure
} from '@phosphor-icons/react';
import axios from 'axios';
import CognitiveMatrixPhase from './CognitiveMatrixPhase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== 24 VIA CHARACTER STRENGTHS ====================
const CHARACTER_STRENGTHS = [
  // WEISHEIT
  { id: 'neugier', name: 'Neugier', category: 'Weisheit', desc: 'Interesse an der Welt, Offenheit fuer Erfahrungen', icon: 'MagnifyingGlass' },
  { id: 'lernliebe', name: 'Liebe zum Lernen', category: 'Weisheit', desc: 'Neue Faehigkeiten und Wissen erwerben', icon: 'BookOpen' },
  { id: 'urteilsvermoegen', name: 'Urteilsvermoegen', category: 'Weisheit', desc: 'Kritisches Denken, Abwaegen aller Seiten', icon: 'Scales' },
  { id: 'erfindungsgeist', name: 'Erfindungsgeist', category: 'Weisheit', desc: 'Kreativitaet, originelle Loesungswege finden', icon: 'Lightbulb' },
  { id: 'soziale_intelligenz', name: 'Soziale Intelligenz', category: 'Weisheit', desc: 'Emotionen anderer lesen und angemessen reagieren', icon: 'Users' },
  { id: 'perspektive', name: 'Perspektive', category: 'Weisheit', desc: 'Weisheit, das grosse Ganze sehen', icon: 'Binoculars' },
  // MUT
  { id: 'tapferkeit', name: 'Tapferkeit', category: 'Mut', desc: 'Sich Bedrohungen und Schmerz stellen', icon: 'Sword' },
  { id: 'ausdauer', name: 'Ausdauer', category: 'Mut', desc: 'Durchhalten trotz Hindernissen', icon: 'Timer' },
  { id: 'authentizitaet', name: 'Authentizitaet', category: 'Mut', desc: 'Ehrlichkeit, Integritaet, Echtheit', icon: 'ShieldCheck' },
  // MENSCHLICHKEIT
  { id: 'liebesfaehigkeit', name: 'Liebesfaehigkeit', category: 'Menschlichkeit', desc: 'Naehe zulassen, tiefe Verbindungen eingehen', icon: 'Heart' },
  { id: 'freundlichkeit', name: 'Freundlichkeit', category: 'Menschlichkeit', desc: 'Grosszuegigkeit, Fuersorglichkeit', icon: 'HandHeart' },
  // GERECHTIGKEIT
  { id: 'teamwork', name: 'Teamwork', category: 'Gerechtigkeit', desc: 'Zusammenarbeit, Loyalitaet zur Gruppe', icon: 'UsersThree' },
  { id: 'fairness', name: 'Fairness', category: 'Gerechtigkeit', desc: 'Gleichbehandlung, gerechtes Handeln', icon: 'Scales' },
  { id: 'fuehrungsvermoegen', name: 'Fuehrungsvermoegen', category: 'Gerechtigkeit', desc: 'Andere motivieren und organisieren', icon: 'Crown' },
  // MAESSIGUNG
  { id: 'vergebungsbereitschaft', name: 'Vergebungsbereitschaft', category: 'Maessigung', desc: 'Verzeihen, zweite Chancen geben', icon: 'Flower' },
  { id: 'bescheidenheit', name: 'Bescheidenheit', category: 'Maessigung', desc: 'Sich nicht in den Vordergrund draengen', icon: 'SmileySad' },
  { id: 'vorsicht', name: 'Vorsicht', category: 'Maessigung', desc: 'Bedachtsamkeit, Risiken abwaegen', icon: 'EyeIcon' },
  { id: 'selbstregulation', name: 'Selbstregulation', category: 'Maessigung', desc: 'Eigene Impulse und Emotionen steuern', icon: 'Target' },
  // TRANSZENDENZ
  { id: 'sinn_fuer_schoenes', name: 'Sinn fuer das Schoene', category: 'Transzendenz', desc: 'Staunen, Ehrfurcht vor Schoenheit und Exzellenz', icon: 'SunHorizon' },
  { id: 'dankbarkeit', name: 'Dankbarkeit', category: 'Transzendenz', desc: 'Bewusstsein fuer das Gute im Leben', icon: 'Sparkle' },
  { id: 'hoffnung', name: 'Hoffnung', category: 'Transzendenz', desc: 'Optimismus, Zuversicht fuer die Zukunft', icon: 'Star' },
  { id: 'humor', name: 'Humor', category: 'Transzendenz', desc: 'Lachen, Leichtigkeit, Verspieltheit', icon: 'SmileyWink' },
  { id: 'spiritualitaet', name: 'Spiritualitaet', category: 'Transzendenz', desc: 'Sinn, Zweck, Verbindung zum Groesseren', icon: 'HandsPraying' },
  { id: 'kreativitaet', name: 'Kreativitaet', category: 'Transzendenz', desc: 'Neue Wege finden, unkonventionell denken', icon: 'Lightning' }
];

const CATEGORY_COLORS = {
  'Weisheit': '#8B5CF6',
  'Mut': '#EF4444',
  'Menschlichkeit': '#EC4899',
  'Gerechtigkeit': '#3B82F6',
  'Maessigung': '#22C55E',
  'Transzendenz': '#F59E0B'
};

// Icon resolver
const StrengthIcon = ({ iconName, size = 24, color }) => {
  const icons = {
    MagnifyingGlass, BookOpen, Scales, Lightbulb, Users, Binoculars,
    Sword, Timer, ShieldCheck, Heart, HandHeart, UsersThree, Crown,
    Flower, SmileySad, EyeIcon, Target, SunHorizon, Sparkle, Star,
    SmileyWink, HandsPraying, Lightning
  };
  const Icon = icons[iconName] || Star;
  return <Icon size={size} weight="duotone" style={{ color }} />;
};

// Integrity questions based on selected strengths
const INTEGRITY_QUESTIONS = {
  tapferkeit: 'Du hast "Tapferkeit" gewaehlt. Wuerdest du fuer eine Ueberzeugung deinen Job riskieren?',
  neugier: 'Du hast "Neugier" gewaehlt. Wann hast du zuletzt etwas voellig Neues ausprobiert, das dir Angst gemacht hat?',
  ausdauer: 'Du hast "Ausdauer" gewaehlt. Beschreibe eine Situation, in der du trotz massivem Widerstand durchgehalten hast.',
  authentizitaet: 'Du hast "Authentizitaet" gewaehlt. Wann hast du zuletzt eine unbequeme Wahrheit ausgesprochen?',
  fuehrungsvermoegen: 'Du hast "Fuehrungsvermoegen" gewaehlt. Wie gehst du mit Situationen um, in denen niemand die Verantwortung uebernehmen will?',
  liebesfaehigkeit: 'Du hast "Liebesfaehigkeit" gewaehlt. Faellt es dir leicht, dich verletzlich zu zeigen?',
  freundlichkeit: 'Du hast "Freundlichkeit" gewaehlt. Hilfst du auch dann, wenn niemand zusieht?',
  teamwork: 'Du hast "Teamwork" gewaehlt. Wie reagierst du, wenn ein Teammitglied nicht seinen Teil beitraegt?',
  fairness: 'Du hast "Fairness" gewaehlt. Wuerdest du einen persoenlichen Vorteil aufgeben, um Gerechtigkeit zu wahren?',
  hoffnung: 'Du hast "Hoffnung" gewaehlt. Was haelt deinen Optimismus aufrecht, wenn alles schieflaeuft?',
  humor: 'Du hast "Humor" gewaehlt. Kannst du ueber dich selbst lachen, auch wenn es wehtut?',
  dankbarkeit: 'Du hast "Dankbarkeit" gewaehlt. Wofuer bist du dankbar, das andere fuer selbstverstaendlich halten?',
  urteilsvermoegen: 'Du hast "Urteilsvermoegen" gewaehlt. Wann hast du zuletzt deine Meinung aufgrund neuer Fakten geaendert?',
  perspektive: 'Du hast "Perspektive" gewaehlt. Wie oft suchst du bewusst die Sichtweise von Menschen, die anders denken?',
  lernliebe: 'Du hast "Liebe zum Lernen" gewaehlt. Was lernst du gerade, das nichts mit deinem Beruf zu tun hat?',
  erfindungsgeist: 'Du hast "Erfindungsgeist" gewaehlt. Beschreibe eine kreative Loesung, auf die du stolz bist.',
  soziale_intelligenz: 'Du hast "Soziale Intelligenz" gewaehlt. Wie merkst du, wenn jemand sich unwohl fuehlt, obwohl er laechelt?',
  vergebungsbereitschaft: 'Du hast "Vergebungsbereitschaft" gewaehlt. Gibt es jemanden, dem du noch nicht vergeben hast?',
  bescheidenheit: 'Du hast "Bescheidenheit" gewaehlt. Wann hast du zuletzt einen Erfolg bewusst nicht erwaehnt?',
  vorsicht: 'Du hast "Vorsicht" gewaehlt. Hindert dich deine Vorsicht manchmal daran, Chancen zu ergreifen?',
  selbstregulation: 'Du hast "Selbstregulation" gewaehlt. Was ist deine groesste Versuchung, und wie gehst du damit um?',
  sinn_fuer_schoenes: 'Du hast "Sinn fuer das Schoene" gewaehlt. Was war der letzte Moment, der dich sprachlos gemacht hat?',
  spiritualitaet: 'Du hast "Spiritualitaet" gewaehlt. Was gibt deinem Leben den tiefsten Sinn?',
  kreativitaet: 'Du hast "Kreativitaet" gewaehlt. Wann fuehlt sich dein Denken am freiesten an?'
};

const NoiseOverlay = () => <div className="noise-overlay" />;

// ==========================================
// PHASE A: SELECTION GRID (Pick 10 of 24)
// ==========================================
const PhaseSelection = ({ onComplete }) => {
  const [selected, setSelected] = useState([]);
  const categories = [...new Set(CHARACTER_STRENGTHS.map(s => s.category))];

  const toggleStrength = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else if (selected.length < 10) {
      setSelected([...selected, id]);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="phase-selection">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold mb-2">Phase A: Vorauswahl</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Waehle die <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>10 Staerken</span>, die dich am besten beschreiben.
        </p>
      </div>

      {/* Counter */}
      <div className="flex items-center justify-between mb-5 px-1">
        <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Ausgewaehlt</span>
        <span className="text-lg font-bold" style={{
          color: selected.length === 10 ? 'var(--accent-green)' : selected.length >= 7 ? 'var(--accent-gold)' : 'var(--text-primary)'
        }}>
          {selected.length}/10
        </span>
      </div>

      {/* Category Sections */}
      {categories.map(cat => (
        <div key={cat} className="mb-5">
          <div className="flex items-center gap-2 mb-2.5 px-1">
            <div className="w-2 h-2 rounded-sm" style={{ background: CATEGORY_COLORS[cat] }} />
            <span className="text-xs font-bold" style={{ color: CATEGORY_COLORS[cat], letterSpacing: '0.05em' }}>{cat.toUpperCase()}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {CHARACTER_STRENGTHS.filter(s => s.category === cat).map(strength => {
              const isSelected = selected.includes(strength.id);
              const isDisabled = !isSelected && selected.length >= 10;
              return (
                <motion.button key={strength.id} whileTap={{ scale: 0.97 }}
                  onClick={() => !isDisabled && toggleStrength(strength.id)}
                  className="text-left p-3 transition-all"
                  style={{
                    background: isSelected ? CATEGORY_COLORS[cat] + '12' : 'var(--bg-surface)',
                    border: `1.5px solid ${isSelected ? CATEGORY_COLORS[cat] : 'var(--border-default)'}`,
                    borderRadius: 'var(--radius-sm)',
                    opacity: isDisabled ? 0.35 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                  data-testid={`strength-${strength.id}`}
                >
                  <div className="flex items-start gap-2">
                    <StrengthIcon iconName={strength.icon} size={18} color={isSelected ? CATEGORY_COLORS[cat] : 'var(--text-secondary)'} />
                    <div className="min-w-0">
                      <p className="text-xs font-bold truncate" style={{
                        color: isSelected ? CATEGORY_COLORS[cat] : 'var(--text-primary)'
                      }}>{strength.name}</p>
                      <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-secondary)', fontSize: '0.65rem' }}>
                        {strength.desc}
                      </p>
                    </div>
                    {isSelected && (
                      <Check size={14} weight="bold" style={{ color: CATEGORY_COLORS[cat], flexShrink: 0 }} />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Submit */}
      <div className="mt-6">
        <button onClick={() => onComplete(selected)} disabled={selected.length !== 10}
          className="page-shell-btn"
          style={{ opacity: selected.length === 10 ? 1 : 0.4 }}
          data-testid="phase-a-submit">
          Weiter zum Ranking <ArrowRight size={18} weight="bold" />
        </button>
        {selected.length !== 10 && (
          <p className="text-xs text-center mt-2" style={{ color: 'var(--text-secondary)' }}>
            Noch {10 - selected.length} Staerken auswaehlen
          </p>
        )}
      </div>
    </motion.div>
  );
};

// ==========================================
// PHASE B: RANKING (Order 10 strengths 1-10)
// ==========================================
const PhaseRanking = ({ selectedIds, onComplete, onBack }) => {
  const [ranked, setRanked] = useState(() =>
    selectedIds.map(id => CHARACTER_STRENGTHS.find(s => s.id === id)).filter(Boolean)
  );

  const moveItem = (index, direction) => {
    const newRanked = [...ranked];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newRanked.length) return;
    [newRanked[index], newRanked[targetIndex]] = [newRanked[targetIndex], newRanked[index]];
    setRanked(newRanked);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="phase-ranking">
      <div className="text-center mb-8">
        <List size={48} className="mx-auto mb-4" weight="duotone" />
        <h2 className="text-xl font-bold mb-2">PHASE B: HIERARCHIE</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Sortiere deine 10 Staerken. <span style={{ color: 'var(--text-primary)' }}>#1 = staerkste Eigenschaft.</span>
        </p>
      </div>

      <div className="space-y-2">
        {ranked.map((strength, index) => {
          const catColor = CATEGORY_COLORS[strength.category];
          const isTop5 = index < 5;
          return (
            <motion.div key={strength.id} layout transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="flex items-center gap-3 p-3"
              style={{
                background: isTop5 ? catColor + '10' : 'var(--bg-surface)',
                border: `1px solid ${isTop5 ? catColor : 'var(--border-default)'}`,
              }}
              data-testid={`rank-item-${strength.id}`}
            >
              {/* Rank Number */}
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0"
                style={{
                  border: `1px solid ${isTop5 ? catColor : 'var(--border-default)'}`,
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 900,
                  fontSize: '0.85rem',
                  color: isTop5 ? catColor : 'var(--text-secondary)'
                }}>
                {index + 1}
              </div>

              {/* Icon + Name */}
              <StrengthIcon iconName={strength.icon} size={20} color={catColor} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate" style={{ color: isTop5 ? catColor : 'var(--text-primary)' }}>
                  {strength.name}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)', fontSize: '0.6rem' }}>
                  {strength.category}
                </p>
              </div>

              {/* Top 5 badge */}
              {index === 4 && (
                <span className="text-mono px-2 py-0.5" style={{ fontSize: '0.55rem', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
                  TOP 5 GRENZE
                </span>
              )}

              {/* Move buttons */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button onClick={() => moveItem(index, -1)} disabled={index === 0}
                  className="p-1 transition-colors hover:bg-black/5 disabled:opacity-20"
                  style={{ border: '1px solid var(--border-default)', background: 'transparent', cursor: index === 0 ? 'default' : 'pointer' }}
                  data-testid={`rank-up-${strength.id}`}>
                  <CaretUp size={14} weight="bold" />
                </button>
                <button onClick={() => moveItem(index, 1)} disabled={index === ranked.length - 1}
                  className="p-1 transition-colors hover:bg-black/5 disabled:opacity-20"
                  style={{ border: '1px solid var(--border-default)', background: 'transparent', cursor: index === ranked.length - 1 ? 'default' : 'pointer' }}
                  data-testid={`rank-down-${strength.id}`}>
                  <CaretDown size={14} weight="bold" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Divider label */}
      <div className="flex items-center gap-3 my-4 px-2">
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>SIGNATUR-STAERKEN = TOP 5</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
      </div>

      <div className="flex gap-3 mt-6">
        <button onClick={onBack} className="page-shell-btn page-shell-btn-outline flex-1" data-testid="phase-b-back">Zurueck</button>
        <button onClick={() => onComplete(ranked.map(s => s.id))}
          className="btn-brutal btn-brutal-filled flex-1 flex items-center justify-center gap-3"
          data-testid="phase-b-submit">
          INTEGRITAETS-CHECK <ArrowRight size={20} weight="bold" />
        </button>
      </div>
    </motion.div>
  );
};

// ==========================================
// PHASE C: INTEGRITY CHECK (3 questions)
// ==========================================
const PhaseIntegrity = ({ rankedIds, ageGroup, onComplete, onBack }) => {
  const top5 = rankedIds.slice(0, 5);
  const [questions] = useState(() => {
    // Pick 3 questions from top 5 strengths
    const available = top5.filter(id => INTEGRITY_QUESTIONS[id]);
    const shuffled = available.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3).map(id => ({
      strengthId: id,
      strength: CHARACTER_STRENGTHS.find(s => s.id === id),
      question: resolveWesenQuestion(id, ageGroup, INTEGRITY_QUESTIONS[id])
    }));
  });
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState('');

  const submitAnswer = () => {
    if (!currentAnswer.trim()) return;
    const newAnswers = [...answers, { strengthId: questions[currentQ].strengthId, answer: currentAnswer.trim() }];
    setAnswers(newAnswers);
    setCurrentAnswer('');
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      onComplete(newAnswers);
    }
  };

  const q = questions[currentQ];
  if (!q) return null;
  const catColor = CATEGORY_COLORS[q.strength.category];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="phase-integrity">
      <div className="text-center mb-8">
        <ShieldCheck size={48} className="mx-auto mb-4" weight="duotone" style={{ color: catColor }} />
        <h2 className="text-xl font-bold mb-2">PHASE C: INTEGRITAETS-CHECK</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Beweise, dass deine Auswahl kein Wunschdenken ist.
        </p>
      </div>

      {/* Progress */}
      <div className="flex gap-2 mb-6">
        {questions.map((_, idx) => (
          <div key={idx} className="flex-1 h-1"
            style={{ background: idx < currentQ ? '#22C55E' : idx === currentQ ? 'var(--text-primary)' : 'var(--border-default)' }} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={currentQ} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
          {/* Strength Badge */}
          <div className="flex items-center gap-3 mb-4 p-3"
            style={{ background: catColor + '10', border: `1px solid ${catColor}` }}>
            <StrengthIcon iconName={q.strength.icon} size={24} color={catColor} />
            <div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>FRAGE {currentQ + 1}/3 · {q.strength.category.toUpperCase()}</p>
              <p className="text-sm font-bold" style={{ color: catColor }}>{q.strength.name}</p>
            </div>
          </div>

          {/* Question */}
          <div className="mb-4 p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
            <p className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)', lineHeight: 1.4 }}>
              {q.question}
            </p>
          </div>

          {/* Answer */}
          <VoiceTextarea
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            placeholder="Deine ehrliche Antwort — schreib oder sprich frei."
            rows={4}
            autoFocus
            testId="integrity-answer"
            className="mb-4"
          />

          <div className="flex gap-3">
            {currentQ === 0 && (
              <button onClick={onBack} className="page-shell-btn page-shell-btn-outline flex-1" data-testid="phase-c-back">Zurueck</button>
            )}
            <button onClick={submitAnswer} disabled={!currentAnswer.trim()}
              className="btn-brutal btn-brutal-filled flex-1 flex items-center justify-center gap-3"
              style={{ opacity: currentAnswer.trim() ? 1 : 0.4 }}
              data-testid="integrity-submit">
              {currentQ < questions.length - 1 ? 'NAECHSTE FRAGE' : 'ASSESSMENT ABSCHLIESSEN'}
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// ==========================================
// RESULTS SCREEN
// ==========================================
const ResultsScreen = ({ rankedIds, integrityAnswers, sessionId, onSubmit, submitting, cognitiveType }) => {
  const top5 = rankedIds.slice(0, 5).map(id => CHARACTER_STRENGTHS.find(s => s.id === id)).filter(Boolean);
  const bottom5 = rankedIds.slice(5).map(id => CHARACTER_STRENGTHS.find(s => s.id === id)).filter(Boolean);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="phase-results">
      <div className="text-center mb-8">
        <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
          <Fingerprint size={64} className="mx-auto mb-4" weight="duotone" style={{ color: '#22C55E' }} />
        </motion.div>
        <h2 className="text-xl font-bold mb-2">DEIN WESEN-PROFIL</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Deine 5 Signatur-Staerken nach dem VIA-Modell.
        </p>
      </div>

      {/* Cognitive Matrix Reveal */}
      {cognitiveType && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mb-6 p-5"
          style={{
            background: 'linear-gradient(135deg, rgba(91,184,196,0.12) 0%, rgba(91,184,196,0.04) 100%)',
            border: '1px solid rgba(91,184,196,0.35)',
            borderRadius: '18px',
          }}
          data-testid="cognitive-type-reveal">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={16} weight="duotone" style={{ color: 'var(--accent-cyan)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>
              KOGNITIVE MATRIX · DEIN TYP
            </span>
          </div>
          <p className="text-2xl font-black mb-1" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            {cognitiveType.deepdive_name}
          </p>
          <p className="text-xs font-mono mb-2" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.15em' }}>
            {cognitiveType.mbti_code} · {cognitiveType.classic_name}
          </p>
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            {cognitiveType.tagline}
          </p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            {cognitiveType.description}
          </p>
        </motion.div>
      )}

      {/* Top 5 Signature Strengths */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3 px-1">
          <Star size={16} weight="fill" style={{ color: '#EAB308' }} />
          <span className="text-overline">SIGNATUR-STAERKEN</span>
        </div>
        <div className="space-y-2">
          {top5.map((s, idx) => {
            const catColor = CATEGORY_COLORS[s.category];
            return (
              <motion.div key={s.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.15 }}
                className="flex items-center gap-3 p-4"
                style={{ background: catColor + '12', border: `1px solid ${catColor}` }}>
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                  style={{ border: `2px solid ${catColor}`, fontFamily: 'var(--font-heading)', fontWeight: 900, fontSize: '1.1rem', color: catColor }}>
                  {idx + 1}
                </div>
                <StrengthIcon iconName={s.icon} size={28} color={catColor} />
                <div>
                  <p className="text-mono font-bold" style={{ color: catColor }}>{s.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.category} · {s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bottom 5 */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3 px-1">
          <span className="text-overline" style={{ color: 'var(--text-secondary)' }}>WeiterE STAERKEN</span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {bottom5.map((s, idx) => (
            <div key={s.id} className="text-center p-2" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
              <StrengthIcon iconName={s.icon} size={20} color={CATEGORY_COLORS[s.category]} />
              <p className="text-mono mt-1" style={{ fontSize: '0.55rem', color: 'var(--text-secondary)' }}>{s.name}</p>
            </div>
          ))}
        </div>
      </div>

      <button onClick={onSubmit} disabled={submitting}
        className="page-shell-btn"
        data-testid="results-submit">
        {submitting ? (
          <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>
            WIRD GESPEICHERT...
          </motion.span>
        ) : (
          <>WESEN-SCAN ABSCHLIESSEN <ArrowRight size={20} weight="bold" /></>
        )}
      </button>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================
const CharacterAssessment = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('matrix'); // matrix | selection | ranking | integrity | results
  const [cognitiveType, setCognitiveType] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [rankedIds, setRankedIds] = useState([]);
  const [integrityAnswers, setIntegrityAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ageGroup, setAgeGroup] = useState(null);

  useEffect(() => {
    // Fetch age_group so we can tailor the integrity questions
    axios.get(`${API}/user/status/${sessionId}`)
      .then(r => setAgeGroup(r.data?.age_group || null))
      .catch(() => setAgeGroup(null));
  }, [sessionId]);

  const handleMatrixComplete = (typeData) => {
    setCognitiveType(typeData);
    setPhase('selection');
    window.scrollTo(0, 0);
  };

  const handleSelectionComplete = (ids) => {
    setSelectedIds(ids);
    setPhase('ranking');
    window.scrollTo(0, 0);
  };

  const handleRankingComplete = (orderedIds) => {
    setRankedIds(orderedIds);
    setPhase('integrity');
    window.scrollTo(0, 0);
  };

  const handleIntegrityComplete = (answers) => {
    setIntegrityAnswers(answers);
    setPhase('results');
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const top5 = rankedIds.slice(0, 5).map(id => {
        const s = CHARACTER_STRENGTHS.find(str => str.id === id);
        return { id, name: s.name, category: s.category };
      });

      const payload = {
        session_id: sessionId,
        selected_strengths: selectedIds,
        ranked_strengths: rankedIds,
        top5_signature: top5,
        integrity_answers: integrityAnswers,
        all_strength_data: rankedIds.map((id, idx) => {
          const s = CHARACTER_STRENGTHS.find(str => str.id === id);
          return { id, name: s.name, category: s.category, rank: idx + 1 };
        })
      };

      await axios.post(`${API}/user/complete-character`, payload);
      await axios.post(`${API}/user/complete-block`, { session_id: sessionId, block_id: 'wesen', answers: payload });
      navigate(`/dashboard/${sessionId}`);
    } catch (err) {
      console.error('Error submitting:', err);
      setSubmitting(false);
    }
  };

  const phaseLabels = ['MATRIX', 'VORAUSWAHL', 'HIERARCHIE', 'INTEGRITAET', 'PROFIL'];
  const phaseIdx = { matrix: 0, selection: 1, ranking: 2, integrity: 3, results: 4 };

  return (
    <div className="page-shell-bg" data-testid="character-assessment">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card">
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Brain size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Block 3: Wesen</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>VIA Character Strengths</p>
            </div>
          </div>
          <span className="text-xs px-3 py-1 font-semibold" style={{ border: '1px solid var(--border-default)', borderRadius: '9999px', color: 'var(--text-secondary)' }}>
            {phaseLabels[phaseIdx[phase]]}
          </span>
        </div>
        <div className="h-1" style={{ background: 'var(--border-default)' }}>
          {phaseLabels.map((_, idx) => null)}
          <div className="h-full" style={{ width: `${((phaseIdx[phase] + 1) / phaseLabels.length) * 100}%`, background: 'var(--accent-cyan)', borderRadius: '0 4px 4px 0', transition: 'width 0.4s' }} />
        </div>
        <div className="page-shell-body">
          <AnimatePresence mode="wait">
            {phase === 'matrix' && <CognitiveMatrixPhase key="matrix" sessionId={sessionId} onComplete={handleMatrixComplete} onBack={() => navigate(`/dashboard/${sessionId}`)} />}
            {phase === 'selection' && <PhaseSelection key="sel" onComplete={handleSelectionComplete} />}
            {phase === 'ranking' && <PhaseRanking key="rank" selectedIds={selectedIds} onComplete={handleRankingComplete} onBack={() => setPhase('selection')} />}
            {phase === 'integrity' && <PhaseIntegrity key="integ" rankedIds={rankedIds} ageGroup={ageGroup} onComplete={handleIntegrityComplete} onBack={() => setPhase('ranking')} />}
            {phase === 'results' && <ResultsScreen key="res" rankedIds={rankedIds} integrityAnswers={integrityAnswers} sessionId={sessionId} onSubmit={handleSubmit} submitting={submitting} cognitiveType={cognitiveType} />}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default CharacterAssessment;
